import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Root, Value, Portal, ItemIndicator } from '@radix-ui/react-select';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
} from '@radix-ui/react-icons';
import {
  SelectRoot,
  SelectTrigger,
  SelectIcon,
  SelectContent,
  SelectViewport,
  SelectItem,
  SelectItemText,
} from './styles';
import {
  SelectScrollUpButton,
  SelectScrollDownButton,
  CreateOptionItem,
  CreateIcon,
} from './styles.creatable';
import { SelectOption as BaseSelectOption } from './RadixSelect';

// Ensure value is always a string for Radix compatibility
export type SelectOption = Omit<BaseSelectOption, 'value'> & { value: string };

export interface RadixCreatableSelectProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onInputChange?: (value: string) => void;
  onCreateOption?: (value: string) => void;
  formatCreateLabel?: (inputValue: string) => string;
  className?: string;
  'aria-label'?: string;
  inputValue?: string;
}

export const RadixCreatableSelect: React.FC<RadixCreatableSelectProps> = ({
  options,
  value,
  defaultValue,
  placeholder,
  disabled,
  onChange,
  onInputChange,
  onCreateOption,
  formatCreateLabel = (inputValue: string) => `Add "${inputValue}"`,
  className,
  'aria-label': ariaLabel,
  inputValue = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentInputValue, setCurrentInputValue] = useState(inputValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentInputValue(inputValue);
  }, [inputValue]);

  const handleInputChange = useCallback(
    (newValue: string) => {
      setCurrentInputValue(newValue);
      onInputChange?.(newValue);
    },
    [onInputChange],
  );

  const handleCreateOption = useCallback(() => {
    if (currentInputValue.trim() && onCreateOption) {
      onCreateOption(currentInputValue.trim());
      setCurrentInputValue('');
    }
  }, [currentInputValue, onCreateOption]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && currentInputValue.trim()) {
        event.preventDefault();
        handleCreateOption();
      }
    },
    [currentInputValue, handleCreateOption],
  );

  const lowerInput = currentInputValue.toLowerCase();
  const filteredOptions = options.filter((option) =>
    option.label.toString().toLowerCase().includes(lowerInput),
  );

  const trimmedInput = currentInputValue.trim();
  const showCreateOption =
    trimmedInput &&
    !options.some(
      (option) => option.label.toString().toLowerCase() === lowerInput,
    );

  return (
    <SelectRoot>
      <Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={onChange}
        disabled={disabled}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <SelectTrigger className={className} aria-label={ariaLabel}>
          <Value placeholder={placeholder} />
          <SelectIcon>
            <ChevronDownIcon />
          </SelectIcon>
        </SelectTrigger>
        <Portal>
          <SelectContent>
            <SelectScrollUpButton>
              <ChevronUpIcon />
            </SelectScrollUpButton>
            <SelectViewport>
              {/* Input field for creating new options */}
              <div style={{ padding: '8px' }}>
                <input
                  ref={inputRef}
                  type="text"
                  value={currentInputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type to create new option..."
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--way-colors-borderColor)',
                    borderRadius: '4px',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  onFocus={(e) => e.stopPropagation()}
                />
              </div>
              {/* Create option */}
              {showCreateOption && (
                <CreateOptionItem
                  value={`__create__${currentInputValue}`}
                  onSelect={handleCreateOption}
                >
                  <CreateIcon />
                  <SelectItemText>
                    {formatCreateLabel(currentInputValue)}
                  </SelectItemText>
                </CreateOptionItem>
              )}
              {/* Existing options */}
              {filteredOptions.map((option, i) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  id={`react-select-2-option-${i}`}
                >
                  <ItemIndicator>
                    <CheckIcon />
                  </ItemIndicator>
                  <SelectItemText>{option.label}</SelectItemText>
                </SelectItem>
              ))}
            </SelectViewport>
            <SelectScrollDownButton>
              <ChevronDownIcon />
            </SelectScrollDownButton>
          </SelectContent>
        </Portal>
      </Root>
    </SelectRoot>
  );
};

export default RadixCreatableSelect;
